'use client';

import { useProject } from '@/contexts/ProjectContext';
import { Sprint, useSprints } from '@/hooks/useSprints';
import { useTaskSprintUpdate } from '@/hooks/useTaskStageUpdate';
import { formatDateCompact, formatDateRange } from '@/utils/dateFormatters';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import SprintModal from './SprintModal';

export default function Sidebar() {
  const queryClient = useQueryClient();
  const { selectedProject, selectedSprint, setSelectedSprint } = useProject();
  const { data: sprints = [], isLoading: loading } = useSprints(selectedProject?.id ?? null);
  const updateSprintMutation = useTaskSprintUpdate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  const [draggedOverSprint, setDraggedOverSprint] = useState<number | null>(null);

  // Restore saved sprint when sprints are loaded
  useEffect(() => {
    if (selectedProject && sprints.length > 0 && !selectedSprint) {
      // Try to restore saved sprint ID from localStorage
      if (typeof window !== 'undefined') {
        const savedSprintId = localStorage.getItem('selected_sprint_id');
        if (savedSprintId) {
          const sprintId = parseInt(savedSprintId, 10);
          const savedSprint = sprints.find(s => s.id === sprintId);
          if (savedSprint) {
            setSelectedSprint(savedSprint);
          }
        }
      }
    }
  }, [sprints, selectedProject, selectedSprint, setSelectedSprint]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const currentSprints = sprints.filter(s => {
    if (!s.start_date || !s.end_date) return false;
    const start = new Date(s.start_date);
    const end = new Date(s.end_date);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return today >= start && today <= end;
  });

  const upcomingSprints = sprints.filter(s => {
    if (!s.start_date) return false;
    const start = new Date(s.start_date);
    start.setHours(0, 0, 0, 0);
    return start > today;
  });

  const completedSprints = sprints.filter(s => {
    if (!s.end_date) return false;
    const end = new Date(s.end_date);
    end.setHours(0, 0, 0, 0);
    return end < today;
  });

  const handleDragOver = (e: React.DragEvent, sprintId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverSprint(sprintId);
  };

  const handleDragLeave = () => {
    setDraggedOverSprint(null);
  };

  const handleDrop = async (e: React.DragEvent, sprintId: number) => {
    e.preventDefault();
    setDraggedOverSprint(null);
    
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;

    updateSprintMutation.mutate({
      taskId: parseInt(taskId, 10),
      sprintId,
      status: 0, // Set to "Not Started" when adding to sprint
    });
  };

  return (
    <>
    <aside className="w-80 bg-slate-800/60 backdrop-blur-sm border-r border-slate-700/50 h-full flex flex-col overflow-hidden">
      <div className="p-4 border-b border-slate-700/50 flex-shrink-0">
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditingSprint(null);
              setIsModalOpen(true);
            }}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium transition-colors text-sm"
          >
            Add new sprint +
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* Current Sprint Section */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Current Sprint
            </h3>
            {loading ? (
              <div className="text-gray-400 text-xs">Loading sprints...</div>
            ) : currentSprints.length > 0 ? (
              <div className="space-y-3">
                {currentSprints.map((sprint) => (
                  <div
                    key={sprint.id}
                    onClick={() => {
                      setSelectedSprint(sprint);
                    }}
                    onDragOver={(e) => handleDragOver(e, sprint.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, sprint.id)}
                    className={`group relative p-3 rounded-lg cursor-pointer transition-all ${
                      selectedSprint?.id === sprint.id
                        ? 'bg-slate-700/60 border border-blue-500/40 border-l-2 border-l-blue-500'
                        : 'bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/50'
                    } ${
                      draggedOverSprint === sprint.id ? 'ring-2 ring-green-500 bg-slate-700/80 border-l-2 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-white font-semibold text-sm transition-colors text-left flex-1 pr-6 ${
                          selectedSprint?.id === sprint.id ? 'text-blue-400' : ''
                        }`}
                      >
                        {sprint.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSprint(sprint);
                          setIsModalOpen(true);
                        }}
                        className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-slate-800 hover:bg-slate-700 rounded z-10"
                        title="Edit sprint"
                      >
                        <svg className="w-2.5 h-2.5 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                    </div>
                    {sprint.start_date && sprint.end_date && (
                      <div className="text-xs text-gray-400 mb-2">
                        {formatDateRange(sprint.start_date, sprint.end_date)}
                      </div>
                    )}
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-1 text-gray-300">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Tasks
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-xs">No current sprint</div>
            )}
          </div>

          {/* Upcoming Section */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Upcoming
            </h3>
            {loading ? (
              <div className="text-gray-400 text-xs">Loading sprints...</div>
            ) : upcomingSprints.length > 0 ? (
              <div className="space-y-3">
                {upcomingSprints.map((sprint) => (
                  <div
                    key={sprint.id}
                    onClick={() => {
                      setSelectedSprint(sprint);
                    }}
                    onDragOver={(e) => handleDragOver(e, sprint.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, sprint.id)}
                    className={`group relative p-3 rounded-lg cursor-pointer transition-all ${
                      selectedSprint?.id === sprint.id
                        ? 'bg-slate-700/60 border border-blue-500/40 border-l-2 border-l-blue-500'
                        : 'bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/50'
                    } ${
                      draggedOverSprint === sprint.id ? 'ring-2 ring-green-500 bg-slate-700/80 border-l-2 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-white font-semibold text-sm transition-colors text-left flex-1 pr-6 ${
                          selectedSprint?.id === sprint.id ? 'text-blue-400' : ''
                        }`}
                      >
                        {sprint.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSprint(sprint);
                          setIsModalOpen(true);
                        }}
                        className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-slate-800 hover:bg-slate-700 rounded z-10"
                        title="Edit sprint"
                      >
                        <svg className="w-2.5 h-2.5 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                    </div>
                    {sprint.start_date && sprint.end_date && (
                      <div className="text-xs text-gray-400 mb-2">
                        {formatDateRange(sprint.start_date, sprint.end_date)}
                      </div>
                    )}
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-1 text-gray-300">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Tasks
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-xs">No upcoming sprints</div>
            )}
          </div>

          {/* Completed Section */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Completed
            </h3>
          {completedSprints.length > 0 ? (
            <div className="space-y-2">
              {completedSprints.map((sprint) => (
                <div
                  key={sprint.id}
                  onClick={() => {
                    setSelectedSprint(sprint);
                  }}
                  onDragOver={(e) => handleDragOver(e, sprint.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, sprint.id)}
                  className={`group relative flex items-center gap-2 p-2 rounded-lg transition-all cursor-pointer ${
                    selectedSprint?.id === sprint.id
                      ? 'bg-slate-700/60 border border-blue-500/40 border-l-2 border-l-blue-500'
                      : 'bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/50'
                  } ${
                    draggedOverSprint === sprint.id ? 'ring-2 ring-green-500 bg-slate-700/80 border-l-2 border-l-green-500' : ''
                  }`}
                >
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span
                    className={`text-white text-sm transition-colors text-left flex-1 pr-6 ${
                      selectedSprint?.id === sprint.id ? 'text-purple-400' : ''
                    }`}
                  >
                    {sprint.name}
                  </span>
                  {sprint.end_date && (
                    <span className="text-gray-400 text-xs">{formatDateCompact(sprint.end_date)}</span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSprint(sprint);
                      setIsModalOpen(true);
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 bg-slate-800 hover:bg-slate-700 rounded z-10"
                    title="Edit sprint"
                  >
                    <svg className="w-2.5 h-2.5 text-gray-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            ) : (
              <div className="text-gray-500 text-xs">No completed sprints</div>
            )}
          </div>
        </div>

    </aside>
    <SprintModal
      isOpen={isModalOpen}
      onClose={() => {
        setIsModalOpen(false);
        setEditingSprint(null);
      }}
      mode={editingSprint ? 'edit' : 'create'}
      sprintId={editingSprint?.id}
      onSprintSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['project', selectedProject?.id, 'sprints'] });
      }}
    />
    </>
  );
}

